"use client";

import { SegmentControl } from "@/components/SegmentControl";
import BlokWrap from "@/components/bloks/BlokWrap";
import { RichTextRender } from "@/components/ui/RichTextRender";
import { Link } from "@/i18n/Link";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { sbLink } from "@/lib/storyblok/link";
import type { BlokFullWidthMedia } from "@/storyblok";
import { type BlokProps, StoryblokComponent } from "@frend-digital/storyblok";
import { clsx } from "@frend-digital/ui";
import type { StoryblokMultilink } from "storyblok";
import { AssetBlok, multiAssetToSingleAsset, sizes } from "../Asset";
import styles from "./FullWidthMedia.module.css";

export const FullWidthMediaBlok = ({
	blok,
	preview,
	firstPublishedAt: _,
	className,
	dataHeight,
	dataHeightMobile,
	...rest
}: BlokProps<BlokFullWidthMedia> & {
	firstPublishedAt?: string;
	className?: string;
	// Set by SideBySideBlok so its own height preset governs the nested media
	// element directly, instead of relying on this blok's own (separate,
	// easily out-of-sync) height field.
	dataHeight?: "small" | "medium" | "large";
	dataHeightMobile?: "small" | "medium" | "large";
}) => {
	const linkData = blok.link ? sbLink(blok.link as StoryblokMultilink) : null;
	const mediaContent = blok.media?.length ? (
		<div
			className={clsx(styles.media)}
			data-object-fit={blok.cropMethod}
			data-height={
				dataHeight ?? ((blok.height || undefined) as "small" | "medium" | "large" | undefined)
			}
			data-height-mobile={
				dataHeightMobile ??
				((blok.heightMobile || undefined) as "small" | "medium" | "large" | undefined)
			}
		>
			<div className={styles.content}>
				<RichTextRender blok={blok.text!} preview={preview} />
				{blok.button?.map((button) => (
					<StoryblokComponent key={button._uid} blok={button} preview={preview} />
				))}
			</div>
			{linkData?.href ? (
				<Link
					href={linkData.href}
					className={styles.mediaLink}
					onClick={() =>
						trackContentClick({
							contentType: blok.component,
							itemId: blok._uid,
							href: linkData.href,
						})
					}
				>
					<AssetBlok
						autoPlay
						muted
						loop
						source={multiAssetToSingleAsset(blok.media)}
						sizes={sizes(100)}
					/>
				</Link>
			) : (
				<AssetBlok
					autoPlay
					muted
					loop
					source={multiAssetToSingleAsset(blok.media)}
					sizes={sizes(100)}
				/>
			)}
		</div>
	) : null;

	return (
		<SegmentControl segments={blok}>
			<BlokWrap editable={blok} as="div" className={clsx(styles.root, className)} {...rest}>
				{mediaContent}
			</BlokWrap>
		</SegmentControl>
	);
};
